import type { z } from "zod";
import type { acceptedContractEnvelopeSchema } from "@excellent/core/verification-protocol-client";
import type { UniversalCompileContractBody } from "../../api/contracts/universal-verification";
import type { SourceIntentReviewBody } from "../../api/contracts/verification-evidence";

type Choice = SourceIntentReviewBody["selections"][number];
type ContextDecision = NonNullable<SourceIntentReviewBody["contextDecisions"]>[number];
export type SourceReviewSnapshot = {
  selections: readonly Choice[];
  contextDecisions?: readonly ContextDecision[];
};
export type SourceReviewAcceptanceIntent = Pick<UniversalCompileContractBody,
  "workItemId" | "version" | "expectedWorkerActorId" | "deploymentMode"
> & Pick<SourceIntentReviewBody, "scenario" | "expectedReviewDigest" | "expectedCompilationDigest"> & {
  reviewId: string;
  expectedReviewDigest: string;
};
export type SourceReviewAcceptanceScope = {
  tenantId: string;
  actorId: string;
  universalTenantId?: string;
};
export type PendingSourceReviewAcceptance = Readonly<{
  fingerprint: string;
  scope: Readonly<SourceReviewAcceptanceScope>;
  body: Readonly<SourceReviewAcceptanceIntent & { idempotencyKey: string }>;
}>;
type AcceptedContract = z.infer<typeof acceptedContractEnvelopeSchema>;
export type SourceReviewAcceptanceSuccess = Readonly<{
  fingerprint: string;
  reviewId: string;
  reviewDigest: string;
  compilationDigest: string;
  contractId: string;
  contractDigest: string;
  sourceTenantId: string;
  universalTenantId: string;
  workItemId: string;
  version: string;
}>;

function snapshotKey(snapshot: SourceReviewSnapshot): string | null {
  const selections = [...snapshot.selections].sort((a, b) => a.predicateId < b.predicateId ? -1 : a.predicateId > b.predicateId ? 1 : 0);
  const decisions = [...(snapshot.contextDecisions ?? [])].sort((a, b) => a.clauseId < b.clauseId ? -1 : a.clauseId > b.clauseId ? 1 : 0);
  if (new Set(selections.map((row) => row.predicateId)).size !== selections.length ||
    new Set(decisions.map((row) => row.clauseId)).size !== decisions.length) return null;
  // Fixed schema order avoids object insertion order changing semantic equality.
  return JSON.stringify([
    selections.map((row) => [row.predicateId, row.scope.repositoryId, row.scope.filePath,
      row.scope.pointer, row.scope.operation, row.scope.unit, row.environmentDigest, row.maximumAgeSeconds]),
    decisions.map((row) => [row.clauseId, row.disposition]),
  ]);
}

/** A client affordance only; the acceptance endpoint must recheck all authority. */
export function sourceReviewMatchesSaved(saved: SourceReviewSnapshot | null | undefined, local: SourceReviewSnapshot): boolean {
  if (!saved) return false;
  const key = snapshotKey(saved);
  return key !== null && key === snapshotKey(local);
}

function universalTenantForSourceScope(scope: SourceReviewAcceptanceScope): string {
  if (scope.universalTenantId) return scope.universalTenantId;
  if (scope.tenantId.startsWith("real:")) return `workspace:${scope.tenantId.slice("real:".length)}`;
  return scope.tenantId;
}

export function prepareSourceReviewAcceptance(
  previous: PendingSourceReviewAcceptance | null,
  scope: SourceReviewAcceptanceScope,
  intent: SourceReviewAcceptanceIntent,
  newKey: () => string = () => `source-review-accept:${crypto.randomUUID()}`,
): PendingSourceReviewAcceptance {
  const body = {
    scenario: intent.scenario, reviewId: intent.reviewId,
    expectedReviewDigest: intent.expectedReviewDigest,
    expectedCompilationDigest: intent.expectedCompilationDigest,
    workItemId: intent.workItemId, version: intent.version,
    expectedWorkerActorId: intent.expectedWorkerActorId, deploymentMode: intent.deploymentMode,
  };
  const normalizedScope = {
    tenantId: scope.tenantId,
    actorId: scope.actorId,
    universalTenantId: universalTenantForSourceScope(scope),
  };
  const fingerprint = JSON.stringify([normalizedScope.tenantId, normalizedScope.actorId, normalizedScope.universalTenantId, body]);
  if (previous?.fingerprint === fingerprint) return previous;
  return Object.freeze({ fingerprint, scope: Object.freeze(normalizedScope),
    body: Object.freeze({ ...body, idempotencyKey: newKey() }) });
}

/** Call with the captured request, not whichever form values are current on return. */
export function bindSourceReviewAcceptanceSuccess(
  request: PendingSourceReviewAcceptance,
  accepted: AcceptedContract,
): SourceReviewAcceptanceSuccess {
  const provenance = accepted.compilationRecord.content.sourceReview;
  const universalTenantId = universalTenantForSourceScope(request.scope);
  if (accepted.lifecycle !== "ACCEPTED" || accepted.content.tenantId !== universalTenantId ||
    accepted.content.workItemId !== request.body.workItemId || accepted.content.version !== request.body.version ||
    accepted.deploymentMode !== request.body.deploymentMode || !provenance ||
    provenance.sourceTenantId !== request.scope.tenantId || provenance.sourceActorId !== request.scope.actorId ||
    provenance.scenario !== request.body.scenario || provenance.reviewId !== request.body.reviewId ||
    provenance.reviewDigest !== request.body.expectedReviewDigest ||
    provenance.compilationDigest !== request.body.expectedCompilationDigest ||
    provenance.candidateContractDigest !== accepted.contractDigest) {
    throw new Error("source-review-acceptance-response-mismatch");
  }
  return Object.freeze({ fingerprint: request.fingerprint, reviewId: request.body.reviewId,
    reviewDigest: request.body.expectedReviewDigest, compilationDigest: request.body.expectedCompilationDigest,
    contractId: accepted.content.contractId, contractDigest: accepted.contractDigest,
    sourceTenantId: provenance.sourceTenantId, universalTenantId,
    workItemId: accepted.content.workItemId, version: accepted.content.version });
}
